import React from 'react';
import { environment } from '@/config/environment';
import { Icon, ICONS } from '@/components/brand';

interface NetworkSwitchDialogProps {
  isOpen: boolean;
  onSwitchNetwork: () => Promise<void>;
  onClose: () => void;
}

export const NetworkSwitchDialog: React.FC<NetworkSwitchDialogProps> = ({ isOpen, onSwitchNetwork, onClose }) => {
  const [isSwitching, setIsSwitching] = React.useState(false);

  if (!isOpen) return null;

  const handleSwitch = async () => {
    setIsSwitching(true);
    try {
      await onSwitchNetwork();
    } catch (error) {
      console.error('Failed to switch network:', error);
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(8, 8, 9, 0.78)', backdropFilter: 'blur(6px)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="network-dialog-title"
    >
      <div
        className="sh-card"
        style={{ maxWidth: 460, width: '100%', margin: '0 16px', padding: '28px 28px 24px' }}
      >
        <div className="flex items-center justify-between" style={{ marginBottom: 18 }}>
          <span className="sh-pill sh-pill-danger">Wrong network</span>
          <button
            type="button"
            onClick={onClose}
            disabled={isSwitching}
            className="sh-btn sh-btn-ghost sh-btn-sm"
            aria-label="Close"
            style={{ padding: 6 }}
          >
            <Icon d={ICONS.close} size={14} />
          </button>
        </div>

        <h2 id="network-dialog-title" className="sh-h2" style={{ marginBottom: 8 }}>
          Switch to <span className="sh-serif" style={{ color: 'var(--accent)' }}>{environment.chainName}</span>
        </h2>
        <p style={{ color: 'var(--text-2)', fontSize: 13, marginBottom: 20 }}>
          The Sahara vesting vault lives on this network. Your wallet must match before claims can be signed.
        </p>

        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--surface-2)',
            padding: '14px 16px',
            marginBottom: 24,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
          }}
        >
          <div>
            <div className="sh-label">Network</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>{environment.chainName}</div>
          </div>
          <div>
            <div className="sh-label">Chain ID</div>
            <div className="sh-mono" style={{ fontSize: 13, marginTop: 6 }}>{environment.chainId}</div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleSwitch}
            disabled={isSwitching}
            className="sh-btn sh-btn-primary"
            style={{ flex: 1 }}
          >
            {isSwitching ? 'Switching…' : 'Switch network'}
            <Icon d={ICONS.arrowRight} size={14} />
          </button>
          <button type="button" onClick={onClose} disabled={isSwitching} className="sh-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
